import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { format, isThisMonth, isThisWeek, isToday, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  BarChart3,
  Clock,
  Eye,
  FileText,
  FlaskConical,
  Pill,
  Search,
  Stethoscope
} from 'lucide-react'

import { useAuth } from '@/context/AuthContext'
import {
  getAnalysesResultats,
  getDiagnostics,
  getPrescriptions,
  getTimelineMedicale,
  searchInMedicalData
} from '@/lib/bulk-document-import'
import { extractMedicalDocumentStoragePath } from '@/lib/medical-document-storage'
import {
  buildMedicalDocumentDisplayDuplicateKey,
  compareMedicalDocumentsForDisplay,
  filterDisplayableMedicalDocuments
} from '@/lib/medical-document-display'
import { getMedicalDocuments } from '@/lib/medical-documents'
import {
  getMedicalDocumentClinicalDate,
  getMedicalDocumentDisplayTitle,
  getMedicalDocumentPrimaryFacts,
  getMedicalDocumentSummary,
  inferMedicalDocumentSpecialty
} from '@/lib/medical-document-presentation'
import type {
  AnalyseResultat,
  Diagnostic,
  MedicalDocument,
  Prescription,
  TimelineMedicale
} from '@/types/health'

type FilterPeriod = 'all' | 'today' | 'week' | 'month' | 'year'
type DataCategory = 'all' | 'documents' | 'prescriptions' | 'analyses' | 'diagnostics'

const CATEGORY_TO_TIMELINE_TYPE: Record<
  Exclude<DataCategory, 'all'>,
  TimelineMedicale['type_event']
> = {
  documents: 'document',
  prescriptions: 'prescription',
  analyses: 'analyse',
  diagnostics: 'diagnostic'
}

export interface MedicalDashboardPreviewTarget {
  id: string
  title: string
  fileUrl: string | null
  documentDate: string
  subtitle?: string | null
}

interface MedicalDataDashboardProps {
  onViewDocument?: (document: MedicalDashboardPreviewTarget) => void
  documents?: MedicalDocument[]
}

function deduplicateMedicalDocuments(documents: MedicalDocument[]): MedicalDocument[] {
  const uniqueDocuments = new Map<string, MedicalDocument>()

  for (const document of filterDisplayableMedicalDocuments(documents)) {
    const duplicateKey = buildMedicalDocumentDisplayDuplicateKey(document)
    const existingDocument = uniqueDocuments.get(duplicateKey)

    if (!existingDocument || compareMedicalDocumentsForDisplay(document, existingDocument) < 0) {
      uniqueDocuments.set(duplicateKey, document)
    }
  }

  return [...uniqueDocuments.values()].sort((left, right) => compareMedicalDocumentsForDisplay(left, right))
}

function parseDateSafely(value: string) {
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function formatDashboardDate(value: string): string {
  const parsed = parseDateSafely(value)
  return parsed ? format(parsed, 'dd MMM yyyy', { locale: fr }) : value
}

function getEventIcon(type: TimelineMedicale['type_event']) {
  switch (type) {
    case 'document':
      return <FileText className="w-4 h-4" />
    case 'prescription':
      return <Pill className="w-4 h-4" />
    case 'analyse':
      return <FlaskConical className="w-4 h-4" />
    case 'diagnostic':
      return <Stethoscope className="w-4 h-4" />
    default:
      return <FileText className="w-4 h-4" />
  }
}

function getEventColor(type: TimelineMedicale['type_event']) {
  switch (type) {
    case 'document':
      return 'border-blue-800/60 bg-blue-900/10 text-blue-300'
    case 'prescription':
      return 'border-emerald-800/60 bg-emerald-900/10 text-emerald-300'
    case 'analyse':
      return 'border-amber-800/60 bg-amber-900/10 text-amber-300'
    case 'diagnostic':
      return 'border-rose-800/60 bg-rose-900/10 text-rose-300'
    default:
      return 'border-dark-border bg-dark-surface text-slate-300'
  }
}

function getPrescriptionStatusColor(statut: Prescription['statut']) {
  switch (statut) {
    case 'actif':
      return 'bg-emerald-900/20 text-emerald-300 border-emerald-800/40'
    case 'termine':
      return 'bg-slate-800/60 text-slate-300 border-slate-700'
    case 'arrete':
      return 'bg-rose-900/20 text-rose-300 border-rose-800/40'
    case 'suspendu':
      return 'bg-amber-900/20 text-amber-300 border-amber-800/40'
    default:
      return 'bg-slate-800/60 text-slate-300 border-slate-700'
  }
}

function getAnalyseStatusColor(statut: AnalyseResultat['statut']) {
  switch (statut) {
    case 'normal':
      return 'bg-emerald-900/20 text-emerald-300 border-emerald-800/40'
    case 'limite':
      return 'bg-amber-900/20 text-amber-300 border-amber-800/40'
    case 'anormal':
      return 'bg-orange-900/20 text-orange-300 border-orange-800/40'
    case 'critique':
      return 'bg-rose-900/20 text-rose-300 border-rose-800/40'
    default:
      return 'bg-slate-800/60 text-slate-300 border-slate-700'
  }
}

export default function MedicalDataDashboard({ onViewDocument, documents: providedDocuments }: MedicalDataDashboardProps) {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all')
  const [selectedCategory, setSelectedCategory] = useState<DataCategory>('all')
  const [showStats, setShowStats] = useState(false)

  const documentsQuery = useQuery({
    queryKey: ['medical-documents-dashboard', user?.id],
    queryFn: () => getMedicalDocuments(user!.id, undefined, 200),
    enabled: Boolean(user && !providedDocuments)
  })

  const prescriptionsQuery = useQuery({
    queryKey: ['prescriptions', user?.id],
    queryFn: () => getPrescriptions(user!.id),
    enabled: Boolean(user)
  })

  const analysesQuery = useQuery({
    queryKey: ['analyses-resultats', user?.id],
    queryFn: () => getAnalysesResultats(user!.id),
    enabled: Boolean(user)
  })

  const diagnosticsQuery = useQuery({
    queryKey: ['diagnostics', user?.id],
    queryFn: () => getDiagnostics(user!.id),
    enabled: Boolean(user)
  })

  const timelineQuery = useQuery({
    queryKey: ['timeline-medicale', user?.id],
    queryFn: () => getTimelineMedicale(user!.id),
    enabled: Boolean(user)
  })

  const searchQuery = useQuery({
    queryKey: ['medical-search', user?.id, searchTerm],
    queryFn: () => searchInMedicalData(user!.id, searchTerm),
    enabled: Boolean(user && searchTerm.length > 2)
  })

  const documents = deduplicateMedicalDocuments(providedDocuments || documentsQuery.data || [])
  const prescriptions = prescriptionsQuery.data || []
  const analyses = analysesQuery.data || []
  const diagnostics = diagnosticsQuery.data || []
  const timeline = timelineQuery.data || []

  const filterByPeriod = (dateStr: string): boolean => {
    if (filterPeriod === 'all') {
      return true
    }

    const date = parseDateSafely(dateStr)
    if (!date) {
      return false
    }

    switch (filterPeriod) {
      case 'today':
        return isToday(date)
      case 'week':
        return isThisWeek(date, { weekStartsOn: 1 })
      case 'month':
        return isThisMonth(date)
      case 'year':
        return date.getFullYear() === new Date().getFullYear()
      default:
        return true
    }
  }

  const categoryMatchesTimeline = (event: TimelineMedicale) => {
    if (selectedCategory === 'all') {
      return true
    }

    return event.type_event === CATEGORY_TO_TIMELINE_TYPE[selectedCategory]
  }

  const filteredDocuments = documents.filter((document) => filterByPeriod(document.document_date))
  const filteredPrescriptions = prescriptions.filter((prescription) =>
    filterByPeriod(prescription.date_prescription)
  )
  const filteredAnalyses = analyses.filter((analysis) => filterByPeriod(analysis.date_analyse))
  const filteredDiagnostics = diagnostics.filter((diagnostic) =>
    filterByPeriod(diagnostic.date_diagnostic)
  )
  const filteredTimeline = timeline.filter(
    (event) => filterByPeriod(event.date_event) && categoryMatchesTimeline(event)
  )

  const compactStats = [
    { label: 'Documents', value: filteredDocuments.length },
    {
      label: 'Prescriptions actives',
      value: filteredPrescriptions.filter((prescription) => prescription.statut === 'actif').length
    },
    {
      label: 'Analyses à surveiller',
      value: filteredAnalyses.filter(
        (analysis) => analysis.statut === 'anormal' || analysis.statut === 'critique'
      ).length
    },
    { label: 'Diagnostics', value: filteredDiagnostics.length }
  ]

  const resolveDocumentForEvent = (event: TimelineMedicale): MedicalDocument | null => {
    const metadata = event.metadata as Record<string, unknown> | null
    const candidateIds = [
      typeof metadata?.document_id === 'string' ? metadata.document_id : null,
      event.type_event === 'document' ? event.id : null
    ].filter((value): value is string => Boolean(value))

    for (const candidateId of candidateIds) {
      const matchingDocument = documents.find((document) => document.id === candidateId)
      if (matchingDocument) {
        return matchingDocument
      }
    }

    const eventStoragePath = extractMedicalDocumentStoragePath(event.file_url)
    if (eventStoragePath) {
      const matchingDocument = documents.find(
        (document) => extractMedicalDocumentStoragePath(document.file_url) === eventStoragePath
      )

      if (matchingDocument) {
        return matchingDocument
      }
    }

    return (
      documents.find(
        (document) =>
          document.title === event.description &&
          document.document_date.slice(0, 10) === event.date_event.slice(0, 10)
      ) || null
    )
  }

  const timelineItems = filteredTimeline.map((event) => {
    const relatedDocument = resolveDocumentForEvent(event)
    const relatedFacts = relatedDocument ? getMedicalDocumentPrimaryFacts(relatedDocument) : []
    const relatedSummary = relatedDocument ? getMedicalDocumentSummary(relatedDocument) : null
    const displayTitle = relatedDocument
      ? getMedicalDocumentDisplayTitle(relatedDocument)
      : event.description
    const subtitle = relatedDocument
      ? relatedFacts.slice(1).join(' · ')
      : [
          formatDashboardDate(event.date_event),
          event.professionnel ? `Dr. ${event.professionnel}` : null,
          event.etablissement
        ]
          .filter(Boolean)
          .join(' · ')
    const secondaryText = relatedDocument
      ? relatedSummary === 'Document médical importé' && relatedDocument.title !== displayTitle
        ? relatedDocument.title
        : relatedSummary
      : null
    const previewTarget =
      onViewDocument && (relatedDocument?.file_url || event.file_url)
        ? {
            id: relatedDocument?.id || event.id,
            title: relatedDocument ? getMedicalDocumentDisplayTitle(relatedDocument) : event.description,
            fileUrl: relatedDocument?.file_url || event.file_url,
            documentDate: relatedDocument
              ? getMedicalDocumentClinicalDate(relatedDocument).rawDate
              : event.date_event,
            subtitle: relatedDocument
              ? relatedFacts.slice(1).join(' · ')
              : [event.category, event.professionnel, event.etablissement].filter(Boolean).join(' · ')
          }
        : null

    return {
      event,
      relatedDocument,
      displayTitle,
      subtitle,
      secondaryText,
      previewTarget,
      specialtyLabel: relatedDocument ? inferMedicalDocumentSpecialty(relatedDocument) : event.category
    }
  }).filter((item) => item.event.type_event !== 'document' || Boolean(item.relatedDocument))

  return (
    <div className="space-y-6">
      <div className="card space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Base médicale</h2>
            <p className="text-sm text-slate-400">Documents, analyses, prescriptions et diagnostics</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full sm:w-72"
              />
            </div>

            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value as FilterPeriod)}
              className="input"
            >
              <option value="all">Toute la période</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as DataCategory)}
              className="input"
            >
              <option value="all">Tout</option>
              <option value="documents">Documents</option>
              <option value="prescriptions">Prescriptions</option>
              <option value="analyses">Analyses</option>
              <option value="diagnostics">Diagnostics</option>
            </select>

            <button
              type="button"
              onClick={() => setShowStats((current) => !current)}
              className="btn-secondary"
            >
              <BarChart3 className="w-4 h-4" />
              Synthèse
            </button>
          </div>
        </div>

        {showStats && (
          <div className="flex flex-wrap gap-2">
            {compactStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5 text-sm text-slate-200"
              >
                <span className="font-semibold text-white">{stat.value}</span> {stat.label}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.8fr)_minmax(280px,1fr)]">
        <div className="card">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Timeline médicale</h3>
              <p className="text-sm text-slate-400">Lecture rapide des événements et accès PDF</p>
            </div>
            <span className="text-xs text-slate-500">{timelineItems.length} événement(s)</span>
          </div>

          <div className="space-y-3 max-h-[38rem] overflow-y-auto pr-1">
            {timelineItems.map(({ event, displayTitle, subtitle, secondaryText, previewTarget, specialtyLabel }, index) => (
              <motion.div
                key={`${event.type_event}-${event.id}-${index}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="rounded-xl border border-dark-border bg-dark-surface-high p-4"
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-lg border p-2 ${getEventColor(event.type_event)}`}>
                    {getEventIcon(event.type_event)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-medium text-white">{displayTitle}</h4>
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] ${getEventColor(event.type_event)}`}
                      >
                        {specialtyLabel}
                      </span>
                    </div>

                    {subtitle && <p className="mt-1 text-sm text-slate-300">{subtitle}</p>}

                    {secondaryText && (
                      <p className="mt-2 text-sm text-slate-400 line-clamp-2">{secondaryText}</p>
                    )}
                  </div>

                  {previewTarget && onViewDocument && (
                    <button
                      type="button"
                      onClick={() => onViewDocument(previewTarget)}
                      className="rounded-lg border border-dark-border bg-dark-surface px-2.5 py-2 text-slate-300 transition-colors hover:text-white"
                      title="Aperçu du PDF"
                      aria-label={`Aperçu ${displayTitle}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}

            {timelineItems.length === 0 && (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">Aucun événement médical sur cette période</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {(selectedCategory === 'all' || selectedCategory === 'prescriptions') && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Pill className="w-5 h-5 text-emerald-400" />
                  Prescriptions
                </h3>
                <span className="text-xs text-slate-400">{filteredPrescriptions.length}</span>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredPrescriptions.slice(0, 10).map((prescription) => (
                  <div key={prescription.id} className="rounded-lg border border-dark-border bg-dark-surface-high p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-white text-sm">{prescription.medicament}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {[prescription.dosage, prescription.frequence].filter(Boolean).join(' · ') || 'Prescription importée'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDashboardDate(prescription.date_prescription)}
                        </p>
                      </div>

                      <span className={`rounded-full border px-2 py-1 text-[11px] ${getPrescriptionStatusColor(prescription.statut)}`}>
                        {prescription.statut}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(selectedCategory === 'all' || selectedCategory === 'analyses') && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-amber-400" />
                  Analyses récentes
                </h3>
                <span className="text-xs text-slate-400">{filteredAnalyses.length}</span>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredAnalyses.slice(0, 8).map((analysis) => (
                  <div key={analysis.id} className="rounded-lg border border-dark-border bg-dark-surface-high p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-white text-sm">{analysis.nom_analyse}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {[analysis.valeur, analysis.unite].filter(Boolean).join(' ')}
                          {analysis.valeur_reference ? ` · Réf ${analysis.valeur_reference}` : ''}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{formatDashboardDate(analysis.date_analyse)}</p>
                      </div>

                      {analysis.statut && (
                        <span className={`rounded-full border px-2 py-1 text-[11px] ${getAnalyseStatusColor(analysis.statut)}`}>
                          {analysis.statut}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(selectedCategory === 'all' || selectedCategory === 'diagnostics') && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-rose-400" />
                  Diagnostics
                </h3>
                <span className="text-xs text-slate-400">{filteredDiagnostics.length}</span>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredDiagnostics.slice(0, 6).map((diagnostic) => (
                  <div key={diagnostic.id} className="rounded-lg border border-dark-border bg-dark-surface-high p-3">
                    <p className="font-medium text-white text-sm">{diagnostic.diagnostic}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDashboardDate(diagnostic.date_diagnostic)}
                      {diagnostic.medecin ? ` · Dr. ${diagnostic.medecin}` : ''}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-full border border-dark-border bg-dark-surface px-2 py-1 text-[11px] text-slate-300">
                        {diagnostic.type_diagnostic}
                      </span>
                      {diagnostic.gravite && (
                        <span className="rounded-full border border-dark-border bg-dark-surface px-2 py-1 text-[11px] text-slate-300">
                          {diagnostic.gravite}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {searchTerm.length > 2 && searchQuery.data && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">
            Résultats pour "{searchTerm}"
          </h3>

          <div className="flex flex-wrap gap-2 text-sm">
            <div className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5 text-slate-300">
              {searchQuery.data.documents.length} document(s)
            </div>
            <div className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5 text-slate-300">
              {searchQuery.data.prescriptions.length} prescription(s)
            </div>
            <div className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5 text-slate-300">
              {searchQuery.data.analyses.length} analyse(s)
            </div>
            <div className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1.5 text-slate-300">
              {searchQuery.data.diagnostics.length} diagnostic(s)
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
